import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Zap, Shirt, Flag, CreditCard, Truck, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
const services = [{
  icon: Zap,
  title: 'DTF Printing & Shipping',
  description: 'High-quality direct-to-film transfers shipped nationwide. Upload your gang sheet or use our builder for vibrant, durable prints on any fabric.',
  href: '/dtf-printing'
}, {
  icon: Shirt,
  title: 'Custom Apparel',
  description: 'T-shirts, hoodies, workwear, and more. Professional apparel for businesses, teams, and organizations with no minimums.',
  href: '/custom-apparel'
}, {
  icon: Flag,
  title: 'Custom Banners',
  description: 'Vinyl, mesh, and fabric banners for events, storefronts, and promotions. Indoor and outdoor options in any size.',
  href: '/custom-banners'
}, {
  icon: CreditCard,
  title: 'Business Cards',
  description: 'Premium business cards with professional finishes. Matte, gloss, foil stamping, and specialty papers available.',
  href: '/business-cards'
}];
export function About() {
  return <div className="min-h-screen bg-atomic-black" data-id="element-453">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden" data-id="element-454">
        {/* Background image */}
        <div className="absolute inset-0 z-0" data-id="element-455">
          <img src="https://cdn.magicpatterns.com/uploads/uva8MEFABuCQVnbqYXPWjj/custom-printed-team-apparel-motorsports-michigan.jpg.jpg" alt="Custom printed Jernigan Racing team apparel — hoodie, t-shirt, hats, and bags" className="absolute inset-0 w-full h-full object-cover" data-id="element-456" />
          <div className="absolute inset-0 bg-atomic-black/80" data-id="element-457" />
          <div className="absolute inset-0 bg-gradient-to-b from-atomic-black/60 via-atomic-black/70 to-atomic-black/90" data-id="element-458" />
        </div>

        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-459">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="max-w-3xl mx-auto text-center" data-id="element-460">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-atomic-green text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-461">
              <Truck size={14} data-id="element-462" />
              <span data-id="element-463">Shipping Nationwide</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight" data-id="element-464">
              About{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-atomic-green to-lime-400" data-id="element-465">
                Print Atomic
              </span>
            </h1>

            <p className="text-xl text-gray-300 leading-relaxed" data-id="element-466">
              Your trusted source for DTF transfers, custom apparel, and printed
              products. Based in Michigan, shipping nationwide to businesses,
              teams, and organizations across the USA.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-20 bg-atomic-black border-t border-white/5" data-id="element-467">
        <div className="container mx-auto px-4 md:px-6" data-id="element-468">
          <div className="max-w-4xl mx-auto" data-id="element-469">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="mb-12" data-id="element-470">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-471">
                Who We Are
              </h2>
              <div className="space-y-4 text-lg text-gray-300 leading-relaxed" data-id="element-472">
                <p data-id="element-473">
                  Print Atomic is a full-service print shop based in Morrice,
                  Michigan, serving customers nationwide. We specialize in DTF
                  transfers, custom apparel, banners, and business cards for
                  racing teams, contractors, small businesses, schools, events,
                  and organizations across the country.
                </p>
                <p data-id="element-474">
                  Whether you're a screen printer looking for quality DTF
                  transfers shipped to your door, a business needing branded
                  workwear, or an organization planning an event — we make
                  custom printing easy, affordable, and reliable. No minimums,
                  no setup fees, and fast turnaround with nationwide shipping.
                </p>
              </div>
            </motion.div>

            {/* What We Do - Updated with 4 Services */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="mb-12" data-id="element-475">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-476">
                What We Do
              </h2>
              <div className="grid md:grid-cols-2 gap-6" data-id="element-477">
                {services.map((service, index) => <motion.a key={service.title} href={service.href} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: index * 0.1
              }} className="group bg-atomic-charcoal rounded-xl p-6 border border-white/10 hover:border-atomic-green/30 transition-colors" data-id="element-478">
                    <div className="w-12 h-12 rounded-lg bg-atomic-green/10 flex items-center justify-center text-atomic-green mb-4 border border-atomic-green/20 group-hover:bg-atomic-green/20 transition-colors" data-id="element-479">
                      <service.icon size={24} data-id="element-480" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2" data-id="element-481">
                      {service.title}
                      <ArrowRight size={16} className="text-atomic-green opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-482" />
                    </h3>
                    <p className="text-gray-400 leading-relaxed" data-id="element-483">
                      {service.description}
                    </p>
                  </motion.a>)}
              </div>
            </motion.div>

            {/* Who We Serve */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="mb-12" data-id="element-484">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-485">
                Who We Serve
              </h2>
              <div className="bg-atomic-charcoal rounded-xl p-8 border border-white/10" data-id="element-486">
                <div className="grid md:grid-cols-2 gap-6" data-id="element-487">
                  <div data-id="element-488">
                    <h3 className="text-lg font-bold text-atomic-green mb-3" data-id="element-489">
                      Screen Printers & Decorators
                    </h3>
                    <p className="text-gray-400 leading-relaxed" data-id="element-490">
                      Quality DTF transfers shipped directly to your shop. Gang
                      sheets, custom sizes, and bulk pricing for resellers.
                    </p>
                  </div>
                  <div data-id="element-491">
                    <h3 className="text-lg font-bold text-atomic-green mb-3" data-id="element-492">
                      Businesses & Contractors
                    </h3>
                    <p className="text-gray-400 leading-relaxed" data-id="element-493">
                      Branded workwear, uniforms, and promotional materials that
                      represent your company professionally.
                    </p>
                  </div>
                  <div data-id="element-494">
                    <h3 className="text-lg font-bold text-atomic-green mb-3" data-id="element-495">
                      Racing Teams & Sports
                    </h3>
                    <p className="text-gray-400 leading-relaxed" data-id="element-496">
                      Crew shirts, pit gear, and team apparel built to handle
                      real use while looking sharp.
                    </p>
                  </div>
                  <div data-id="element-497">
                    <h3 className="text-lg font-bold text-atomic-green mb-3" data-id="element-498">
                      Schools & Organizations
                    </h3>
                    <p className="text-gray-400 leading-relaxed" data-id="element-499">
                      Spirit wear, fundraising apparel, event merchandise, and
                      promotional products with no minimums.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Why Choose Us */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="mb-12" data-id="element-500">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-501">
                Why Choose Print Atomic?
              </h2>
              <div className="space-y-4" data-id="element-502">
                <div className="flex items-start gap-4 bg-atomic-charcoal rounded-lg p-6 border border-white/10" data-id="element-503">
                  <div className="text-atomic-green mt-1" data-id="element-504">✓</div>
                  <div data-id="element-505">
                    <h3 className="text-lg font-bold text-white mb-2" data-id="element-506">
                      Nationwide Shipping
                    </h3>
                    <p className="text-gray-400" data-id="element-507">
                      Fast, reliable shipping across the USA. DTF transfers
                      typically ship within 24-48 hours.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-atomic-charcoal rounded-lg p-6 border border-white/10" data-id="element-508">
                  <div className="text-atomic-green mt-1" data-id="element-509">✓</div>
                  <div data-id="element-510">
                    <h3 className="text-lg font-bold text-white mb-2" data-id="element-511">
                      Free Design Help
                    </h3>
                    <p className="text-gray-400" data-id="element-512">
                      No artwork? No problem. We help clean up, adjust, or
                      create designs so your products look right before
                      production.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-atomic-charcoal rounded-lg p-6 border border-white/10" data-id="element-513">
                  <div className="text-atomic-green mt-1" data-id="element-514">✓</div>
                  <div data-id="element-515">
                    <h3 className="text-lg font-bold text-white mb-2" data-id="element-516">
                      No Minimums
                    </h3>
                    <p className="text-gray-400" data-id="element-517">
                      Order one item or thousands. We support small orders and
                      large production runs alike.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-atomic-charcoal rounded-lg p-6 border border-white/10" data-id="element-518">
                  <div className="text-atomic-green mt-1" data-id="element-519">✓</div>
                  <div data-id="element-520">
                    <h3 className="text-lg font-bold text-white mb-2" data-id="element-521">
                      No Setup Fees
                    </h3>
                    <p className="text-gray-400" data-id="element-522">
                      Transparent pricing. You pay for what you order — no
                      surprise setup charges or hidden fees.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-atomic-charcoal rounded-lg p-6 border border-white/10" data-id="element-523">
                  <div className="text-atomic-green mt-1" data-id="element-524">✓</div>
                  <div data-id="element-525">
                    <h3 className="text-lg font-bold text-white mb-2" data-id="element-526">
                      Built for Real Use
                    </h3>
                    <p className="text-gray-400" data-id="element-527">
                      Our products are made for people who actually use their
                      gear — durable, professional, and built to last.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-atomic-charcoal border-t border-white/5" data-id="element-528">
        <div className="container mx-auto px-4 md:px-6" data-id="element-529">
          <div className="max-w-4xl mx-auto" data-id="element-530">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center mb-12" data-id="element-531">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-532">
                Get in Touch
              </h2>
              <p className="text-xl text-gray-300 mb-8" data-id="element-533">
                Ready to get started? We're here to help with your next project.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6 mb-12" data-id="element-534">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.1
            }} className="bg-atomic-black rounded-xl p-6 border border-white/10 text-center" data-id="element-535">
                <div className="w-12 h-12 rounded-lg bg-atomic-green/10 flex items-center justify-center text-atomic-green mx-auto mb-4 border border-atomic-green/20" data-id="element-536">
                  <MapPin size={24} data-id="element-537" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2" data-id="element-538">Location</h3>
                <p className="text-gray-400" data-id="element-539">
                  320 Main Street
                  <br data-id="element-540" />
                  Morrice, MI 48857
                </p>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.2
            }} className="bg-atomic-black rounded-xl p-6 border border-white/10 text-center" data-id="element-541">
                <div className="w-12 h-12 rounded-lg bg-atomic-green/10 flex items-center justify-center text-atomic-green mx-auto mb-4 border border-atomic-green/20" data-id="element-542">
                  <Phone size={24} data-id="element-543" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2" data-id="element-544">Phone</h3>
                <a href="tel:517-414-9232" className="text-gray-400 hover:text-atomic-green transition-colors" data-id="element-545">
                  517-414-9232
                </a>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.3
            }} className="bg-atomic-black rounded-xl p-6 border border-white/10 text-center" data-id="element-546">
                <div className="w-12 h-12 rounded-lg bg-atomic-green/10 flex items-center justify-center text-atomic-green mx-auto mb-4 border border-atomic-green/20" data-id="element-547">
                  <Mail size={24} data-id="element-548" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2" data-id="element-549">Email</h3>
                <a href="mailto:info@printatomic.com" className="text-gray-400 hover:text-atomic-green transition-colors" data-id="element-550">
                  info@printatomic.com
                </a>
              </motion.div>
            </div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4
          }} className="text-center" data-id="element-551">
              <a href="/contact" data-id="element-552">
                <Button size="lg" className="text-lg px-10" data-id="element-553">
                  Get a Quote
                </Button>
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </div>;
}